import { useLocation, useInRouterContext } from "react-router-dom";
import { usePublicServerData } from "@/features/content/context/PublicServerDataContext";

export type Locale = "pt" | "en";

export function resolveLocale(
  pathname?: string | null,
  serverDataLocale?: "pt" | "en" | null,
): Locale {
  if (pathname) {
    // Normaliza para verificar pathname mesmo se vier com query ou hash
    let path = pathname;
    const queryIndex = path.indexOf("?");
    if (queryIndex !== -1) path = path.slice(0, queryIndex);
    const hashIndex = path.indexOf("#");
    if (hashIndex !== -1) path = path.slice(0, hashIndex);

    if (path === "/en" || path.startsWith("/en/")) {
      return "en";
    }
    if (path === "/pt" || path.startsWith("/pt/")) {
      return "pt";
    }
    return "pt";
  }

  if (serverDataLocale === "en") {
    return "en";
  }

  return "pt";
}

export function useLocale(): Locale {
  const inRouter = useInRouterContext();
  const location = inRouter ? useLocation() : null;
  const serverData = usePublicServerData();
  return resolveLocale(location?.pathname, serverData?.locale);
}

export function setLocaleCookie(locale: Locale): void {
  if (typeof document !== "undefined") {
    document.cookie = "au_locale=" + locale + "; path=/; max-age=31536000; samesite=lax";
  }
}

export function getLocalizedPath(pathname: string, targetLocale: Locale): string {
  if (!pathname) {
    return targetLocale === "en" ? "/en" : "/";
  }

  // Preserva query e hash se presentes
  let path = pathname;
  let searchAndHash = "";
  const match = path.match(/[?#].*$/);
  if (match && match.index !== undefined) {
    searchAndHash = path.slice(match.index);
    path = path.slice(0, match.index);
  }

  // Remove prefixo /en ou /pt
  let stripped = path;
  if (stripped === "/en" || stripped === "/pt") {
    stripped = "/";
  } else if (stripped.startsWith("/en/")) {
    stripped = stripped.slice(3);
  } else if (stripped.startsWith("/pt/")) {
    stripped = stripped.slice(3);
  }

  if (!stripped.startsWith("/")) {
    stripped = "/" + stripped;
  }

  let basePath = "";
  if (targetLocale === "en") {
    basePath = stripped === "/" ? "/en" : "/en" + stripped;
  } else {
    basePath = stripped === "/" ? "/" : stripped;
  }

  return basePath + searchAndHash;
}

export function localizeHref(href: string, locale: Locale): string {
  if (!href) return href;
  if (href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return href;
  }
  if (href.startsWith("#")) {
    return locale === "en" ? `/en${href}` : href;
  }
  return getLocalizedPath(href, locale);
}
